import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemText, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import PageLayout from '../shell/PageLayout';
import FormRow from '../common/components/FormRow';
import { request, useOperations } from './OperationsProvider';
import OperationsStatus from './OperationsStatus';
import { deviceAvailable } from './devices';
import useOperationalPermissions from '../common/util/operationalPermissions';
import IntegrationsPanel from './IntegrationsPanel';
import BrandingPanel from './BrandingPanel';
import AreaPanel from './AreaPanel';
import CheckpointFence from './CheckpointFence';
import ShiftRules from './ShiftRules';
import ImportRecords from './ImportRecords';
import { importable } from './importSpecs';
import { downloadRegistry, downloadable } from './registryExport';
import SchedulePanel from './SchedulePanel';
import EmailPanel from './EmailPanel';
import TrackingProviderPanel from './TrackingProviderPanel';
import VehicleTable from './VehicleTable';
import DeviceTable from './DeviceTable';
import UserTable from './UserTable';
import { formatDate, formatDateTime } from '../common/util/datetime';

const fields = {
  vehicles: [['name', 'Vehicle reference']],
  // No tracking reference here any more. A GPS unit is chosen from the devices the platform has
  // made available to this workspace, below - typing a reference was how a device came to sit in the
  // register bound to nothing, looking registered and reporting nothing for a week.
  sources: [['identifier', 'Device identifier'], ['kind', 'Type', ['GPS', 'RFID', 'EXTERNAL']], ['site', 'Site (optional)'], ['simNumber', 'SIM number (optional)'], ['model', 'Model (optional)']],
  drivers: [['name', 'Driver name'], ['reference', 'Driver reference']],
  transporters: [['name', 'Transporter name'], ['contactName', 'Contact name (optional)'], ['contactMobile', 'Contact mobile (optional)'], ['gstin', 'GSTIN (optional)']],
  readers: [['name', 'Reader name'], ['identifier', 'Unique reader identifier'], ['model', 'Reader model (optional)'], ['heartbeatSeconds', 'Offline threshold (seconds)']],
  catalog: [['list', 'List', ['material', 'consignor', 'consignee', 'destination', 'transporter']], ['name', 'Name']],
  checkpoints: [['name', 'Checkpoint name'], ['site', 'Site'], ['type', 'Stage type', ['GATE_IN', 'SECURITY', 'WEIGHBRIDGE', 'BAY', 'GATE_OUT', 'CLIENT']], ['handles', 'Traffic handled', ['BOTH', 'INBOUND', 'OUTBOUND']], ['dwellMinutes', 'Dwell threshold in minutes (optional)'], ['latitude', 'Latitude (optional)'], ['longitude', 'Longitude (optional)'], ['radiusMeters', 'Geofence radius in metres (optional)']],
  routes: [['name', 'Route name'], ['transitMinutes', 'Next-milestone timeout in minutes (optional)']],
  // TPMS_OPERATOR is offered here but admits its holder nowhere else in this product: it reaches the
  // tyre screens and is refused by every operational endpoint, including every one that carries a
  // position. A tyre fitter is not a tracking user.
  users: [['name', 'Name'], ['email', 'Email'], ['password', 'Password (at least 12 characters)'], ['role', 'Role', ['STANDARD_USER', 'PLANT_OPERATOR', 'TENANT_ADMINISTRATOR', 'TRANSPORTER', 'TPMS_OPERATOR']]],
  config: [['vivoSlaMinutes', 'Plant turnaround SLA in minutes'], ['vivoRiskMinutes', 'Turnaround risk threshold in minutes'], ['shareLifetimeMinutes', 'Tracking link lifetime in minutes'], ['reportingIntervalMinutes', 'Source reporting interval in minutes'], ['dataDelayMinutes', 'Data delay threshold in minutes'], ['plantSpeedKph', 'Plant speed limit (km/h)'], ['roadSpeedKph', 'Road speed limit (km/h)'], ['longStayMinutes', 'Too long inside the plant, in minutes (optional)']],
};
const GROUPS = [
  ['The plant', ['checkpoints', 'areas', 'routes', 'readers']],
  ['The desks', ['catalog', 'transporters', 'config', 'shifts']],
  ['Reporting', ['schedules', 'email']],
  ['Access and connections', ['users', 'tracking', 'integrations', 'branding']],
  ['Vehicles', ['vehicles', 'drivers', 'sources', 'assignments']],
];
// The registries whose entries can be corrected and cleared here. The rest are either records of
// what happened, which are not edited, or have screens of their own.
// The rules this panel reports, in the order somebody reads them, with the unit each is kept in.
// Everything else on the config record - its id, its tenant, the shift pattern - is not a threshold.
const THRESHOLDS = [
  ['vivoSlaMs', 'Plant turnaround SLA', 'min'],
  ['vivoRiskMs', 'Turnaround risk threshold', 'min'],
  ['longStayMs', 'Too long inside the plant', 'min'],
  ['parkingDwellMs', 'Waiting at the parking before it is noted', 'min'],
  ['presenceTimeoutMs', 'A fix is stale after', 'min'],
  ['reportingIntervalMs', 'A tracker is quiet after', 'min'],
  ['dataDelayMs', 'Data delay threshold', 'min'],
  ['shareLifetimeMs', 'Tracking link lifetime', 'min'],
  ['plantSpeedKph', 'Plant speed limit', 'kph'],
  ['roadSpeedKph', 'Road speed limit', 'kph'],
];
const AMENDABLE = ['checkpoints', 'routes', 'drivers', 'transporters', 'readers', 'catalog'];
const names = {
  vehicles: 'Vehicles', sources: 'Devices', assignments: 'Assignments', areas: 'Areas & geofences', catalog: 'Master lists', checkpoints: 'Checkpoints', readers: 'RFID readers', routes: 'Route templates', drivers: 'Drivers', transporters: 'Transporters', config: 'Operational rules', shifts: 'Shifts', schedules: 'Scheduled reports', email: 'Email delivery', users: 'User access', tracking: 'Tracking provider', integrations: 'Integrations', branding: 'Logo & branding',
};
const AdministrationPage = ({ area }) => {
  const navigate = useNavigate();
  const { data, trips, mutate, refresh, updatedAt, error: unavailable } = useOperations();
  // The GPS units the platform has granted this workspace. Not the whole registry - that is the
  // platform's hardware inventory, and one customer has no business reading another's.
  const [available, setAvailable] = useState([]);
  useEffect(() => {
    request('/api/operations/tracking/available-devices').then(setAvailable).catch(() => setAvailable([]));
  }, []);
  const currentUserId = useSelector((state) => state.session.user?.id);
  const { administer, operate, haulier } = useOperationalPermissions();
  // The tab lives in the address, so a finding that counts devices can open the device register
  // rather than the vehicle list with the devices one unexplained click away.
  const [params, setParams] = useSearchParams();
  const asked = params.get('tab');
  const tab = asked && names[asked] ? asked : (area === 'configuration' ? 'checkpoints' : 'vehicles');
  const setTab = (value) => { const next = new URLSearchParams(params); next.set('tab', value); setParams(next); };
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  // Which stage's fence is being drawn, if any. Fences are edited on a map rather than in the form
  // that creates a checkpoint: a lane is a shape somebody traces over the satellite view of their
  // own plant, not four numbers they can type.
  const [fencing, setFencing] = useState(null);
  // A stage is set up once and lives for years, which is exactly why it needs correcting: a gate is
  // renamed, a bay becomes a weighbridge, a gate kept for deliveries starts taking despatch too.
  const [amending, setAmending] = useState(null);
  const [removing, setRemoving] = useState(null);
  // Which register is being loaded from a file, if any.
  const [loading, setLoading] = useState(null);
  // Amending uses the same form that created the record, filled in with what is there now. Times are
  // held in milliseconds and shown in minutes, which is the one translation the form does.
  const startEdit = (row) => {
    const filled = { ...row };
    fields[tab].forEach(([key]) => { if (key.endsWith('Minutes')) filled[key] = row[key.replace('Minutes', 'Ms')] ? row[key.replace('Minutes', 'Ms')] / 60000 : ''; });
    setAmending(row); setForm(filled); setError(null); setDialog(true);
  };
  const remove = async () => {
    setPending(true); setError(null);
    try { await mutate(`${tab}/${removing.id}`, null, 'DELETE'); setRemoving(null); } catch (err) { setError(err.message); } finally { setPending(false); }
  };
  const [health, setHealth] = useState('');
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  // Areas are created on the map inside their own panel, not through the generic form.
  const canAdd = !['integrations', 'branding', 'areas', 'tracking', 'schedules', 'email', 'shifts'].includes(tab) && (administer || (['assignments', 'vehicles'].includes(tab) && operate) || (tab === 'vehicles' && haulier));
  // The rules are not created, only corrected: there is one record and the plant is already running
  // on it. So the form opens showing what is in force, the way the edit of any other entry does -
  // an empty form invited somebody to retype four thresholds to change one, and to leave the rest
  // blank without meaning to.
  const rulesNow = () => {
    const filled = {};
    fields.config.forEach(([key]) => {
      const stored = key.endsWith('Minutes') ? data.config?.[key.replace('Minutes', 'Ms')] : data.config?.[key];
      filled[key] = stored == null || stored === '' ? '' : (key.endsWith('Minutes') ? stored / 60000 : stored);
    });
    return filled;
  };
  const start = () => {
    if (tab === 'vehicles') { navigate('/vehicles/register'); return; }
    setAmending(null);
    if (tab === 'config') setForm(rulesNow());
    else setForm(tab === 'assignments' ? { mode: 'TEMPORARY' } : {});
    setError(null); setDialog(true);
  };
  const registerRead = (read) => { setForm({ identifier: read.identifier, kind: read.kind }); setError(null); setDialog(true); };
  const submit = async (event) => {
    event.preventDefault(); setPending(true); setError(null);
    try {
      const input = { ...form };
      // A blank threshold is sent as a blank rather than left out, because leaving it out now means
      // "do not touch this one" and emptying it has to stay a way of turning a rule off.
      Object.keys(input).filter((key) => key.endsWith('Minutes')).forEach((key) => { input[key.replace('Minutes', 'Ms')] = input[key] === '' || input[key] == null ? '' : Number(input[key]) * 60000; delete input[key]; });
      if (tab === 'routes') input.checkpointIds = form.checkpointIds || [];
      if (tab === 'users') input.vehicleIds = form.vehicleIds || [];
      if (amending) await mutate(`${tab}/${amending.id}`, input, 'PUT');
      else await mutate(tab, input);
      setDialog(false); setAmending(null);
    } catch (err) { setError(err.message); } finally { setPending(false); }
  };
  const action = async (id, verb) => {
    setPending(true); setError(null);
    try { await mutate(`assignments/${id}/${verb}`, {}); } catch (err) { setError(err.message); } finally { setPending(false); }
  };
  const groups = GROUPS
    .map(([group, keys]) => [group, keys.filter((key) => (area === 'vehicles' ? ['vehicles', 'drivers', 'sources', 'assignments'].includes(key) : !['vehicles', 'drivers', 'sources', 'assignments'].includes(key))
      && (administer || !['users', 'integrations', 'branding', 'tracking', 'schedules', 'email'].includes(key))
      && (!haulier || key === 'vehicles'))])
    .filter(([, keys]) => keys.length);
  // What this tab is showing, after the search and the reader-health filter. The list below and the
  // download button read the same thing, so a file can never hold rows the screen did not.
  // Only the tabs that hold a list of entries have one to narrow. Operational rules, branding and
  // the rest are single records, and asking an object to filter itself is how this page came to
  // render as nothing at all.
  const shown = (Array.isArray(data[tab]) ? data[tab] : []).filter((row) => JSON.stringify(row).toLowerCase().includes(search.toLowerCase()) && (tab !== 'readers' || !health || row.health === health));
  const recordName = (kind, id) => data[kind]?.find((row) => row.id === id)?.name || data[kind]?.find((row) => row.id === id)?.identifier || 'Unavailable';
  return (
    <PageLayout title={area === 'vehicles' ? 'Vehicles' : 'Configuration'} subtitle={area === 'vehicles' ? 'Vehicle registry, drivers and tracking assignments.' : 'Checkpoints, integrations and plant operating rules.'} actions={canAdd && <Button variant="contained" disabled={!updatedAt || !!unavailable} onClick={start}>{tab === 'config' ? 'Configure rules' : `Add ${tab === 'assignments' ? 'assignment' : tab === 'sources' ? 'device' : tab.replace(/s$/, '')}`}</Button>}>
      <OperationsStatus />
      {!dialog && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '232px 1fr' }, gap: 3, alignItems: 'start' }}>
        {/* The sections a plant is set up in, rather than one strip of everything. On a phone the
            same list stacks above the panel, which is the one place a long list is the right shape. */}
        <Paper variant="outlined" sx={{ p: 1, position: { md: 'sticky' }, top: { md: 16 } }}>
          {groups.map(([group, keys]) => (
            <Box key={group} sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.6 }}>{group}</Typography>
              <List dense disablePadding>
                {keys.map((key) => (
                  <ListItemButton
                    key={key}
                    selected={tab === key}
                    onClick={() => { setTab(key); setSearch(''); }}
                    sx={{ borderRadius: 1, py: 0.6 }}
                  >
                    <ListItemText primary={names[key]} primaryTypographyProps={{ variant: 'body2', fontWeight: tab === key ? 700 : 400 }} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          ))}
        </Paper>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ mb: tab === 'catalog' ? 0.5 : 2 }}>{names[tab]}</Typography>
          {tab === 'catalog' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Material, consignor, consignee, destination and transporter, as the gate and yard desks
              are offered them. Entries are learned from the trips the plant runs; correct or remove
              one here and the desks stop being offered it.
            </Typography>
          )}
          {!['config', 'integrations', 'branding', 'schedules', 'email', 'shifts'].includes(tab) && (
          <Stack direction="row" gap={2} sx={{ mb: 2 }} alignItems="center">
            <TextField label={`Search ${names[tab].toLowerCase()}`} value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1 }} />
            {/* A plant arrives with its fleet in a spreadsheet, not one truck at a time. Devices
                have their own button inside their table, which also checks each tracking
                reference as it reads it. */}
            {importable(tab) && administer && <Button variant="outlined" onClick={() => setLoading(tab)}>Import CSV</Button>}
            {/* What is on screen, as a spreadsheet. Narrowed by the search above, because a file
                holding more than the screen did is one nobody can reconcile. */}
            {downloadable(tab) && <Button variant="outlined" onClick={() => downloadRegistry(tab, shown, data)}>Download CSV</Button>}
            {tab === 'readers' && (
            <TextField label="Reader health" select value={health} onChange={(e) => setHealth(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All health states</MenuItem>
              {['ONLINE', 'OFFLINE', 'DEGRADED', 'NEVER_SEEN'].map((v) => <MenuItem key={v} value={v}>{v.replaceAll('_', ' ')}</MenuItem>)}
            </TextField>
            )}
          </Stack>
          )}
          {tab === 'shifts' ? <ShiftRules /> : tab === 'schedules' ? <SchedulePanel /> : tab === 'email' ? <EmailPanel /> : tab === 'integrations' ? <IntegrationsPanel /> : tab === 'branding' ? <BrandingPanel /> : tab === 'areas' ? <AreaPanel /> : tab === 'tracking' ? <TrackingProviderPanel /> : tab === 'users' ? (
            <UserTable
              rows={(data.users || []).filter((row) => JSON.stringify(row).toLowerCase().includes(search.toLowerCase()))}
              currentUserId={currentUserId}
              onChanged={refresh}
            />
          ) : tab === 'vehicles' ? (
            <VehicleTable
              rows={(data.vehicles || []).filter((row) => JSON.stringify(row).toLowerCase().includes(search.toLowerCase()))}
              assignments={data.assignments}
              sources={data.sources}
              trips={trips}
              documents={params.get('documents') || ''}
              onClearDocuments={() => { const next = new URLSearchParams(params); next.delete('documents'); setParams(next); }}
            />
          ) : tab === 'sources' ? (
            <DeviceTable
              rows={(data.sources || []).filter((row) => JSON.stringify(row).toLowerCase().includes(search.toLowerCase()))}
              administer={administer}
              operate={operate}
              onRegister={registerRead}
              reporting={params.get('reporting') || ''}
              onClearReporting={() => { const next = new URLSearchParams(params); next.delete('reporting'); setParams(next); }}
            />
          ) : tab === 'config' ? (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Configured thresholds</Typography>
                {/* The page-level button says the same thing, but a reader looking at a value they
                    want to change looks for the control beside the value. */}
                {administer && <Button size="small" disabled={!updatedAt || !!unavailable} onClick={start}>Edit rules</Button>}
              </Stack>
              {/* Only the rules, named as the form names them and carrying their own units. The
                  record also holds its own id, the tenant it belongs to and the plant's shifts,
                  which are not thresholds and were being printed as speeds. */}
              {THRESHOLDS.map(([key, label, unit]) => (
                <Stack key={key} direction="row" justifyContent="space-between" sx={{ py: 0.75, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {data.config[key] == null || data.config[key] === ''
                      ? 'Not set'
                      : unit === 'min' ? `${Math.round(data.config[key] / 60000)} min` : `${data.config[key]} km/h`}
                  </Typography>
                </Stack>
              ))}
              {!!(data.config.shifts || []).length && (
                <Stack direction="row" justifyContent="space-between" sx={{ py: 0.75, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary">Shifts</Typography>
                  <Typography variant="body2" fontWeight={600}>{data.config.shifts.map((shift) => `${shift.name} ${shift.start}`).join(' · ')}</Typography>
                </Stack>
              )}
              {!data.config.id && <Typography color="text.secondary" sx={{ mt: 2 }}>No thresholds configured. Timing and compliance measures remain unevaluated until rules are set.</Typography>}
            </Paper>
          )
            : (
              <Stack gap={1.5}>
                {shown.map((row) => (
                  <Paper key={row.id} variant="outlined" sx={{ p: 2.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                      <Box>
                        <Typography fontWeight={600}>{tab === 'assignments' ? `${recordName('vehicles', row.vehicleId)} · ${recordName('sources', row.sourceId)}` : row.name || row.identifier}</Typography>
                        {tab === 'vehicles' && (
                        <Stack gap={1} sx={{ mt: 1 }}>
                          {row.photoDataUrl && <Box component="img" src={row.photoDataUrl} alt={row.name} sx={{ width: 140, height: 90, objectFit: 'contain' }} />}
                          <Typography variant="body2">{[row.vehicleType, row.owner, row.transporter, row.driverName, row.driverMobile].filter(Boolean).join(' · ')}</Typography>
                          <Stack direction="row" gap={1} flexWrap="wrap">{row.documents?.map((d) => <Chip key={d.kind} size="small" color={new Date(`${d.expiresOn}T23:59:59`) < new Date() ? 'error' : 'default'} label={`${d.kind} · ${d.reference} · Expires ${d.expiresOn}`} />)}</Stack>
                        </Stack>
                        )}
                        <Typography variant="body2" color="text.secondary">{tab === 'assignments' ? `${row.mode} · ${row.kind} · Assigned ${formatDateTime(row.startedAt)}` : tab === 'catalog' ? `${row.list}${row.learned ? ' · learned from a trip' : ''}` : row.kind || row.reference || row.site || ''}</Typography>
                        {tab === 'checkpoints' && (
                        <Typography variant="caption" color={row.shape === 'POLYLINE' || row.radiusMeters > 0 ? 'text.secondary' : 'warning.main'}>
                          {row.shape === 'POLYLINE'
                            ? `Lane · ${(row.path || []).length} points · ${Math.round((row.corridorMeters || 0) * 2)} m wide`
                            : row.radiusMeters > 0
                              ? `Circle · ${row.radiusMeters} m radius`
                              : 'No geofence, so GPS cannot record this stage'}
                        </Typography>
                        )}
                        {tab === 'sources' && row.providerId && <Typography variant="caption">{`Tracking reference: ${row.providerId}`}</Typography>}
                        {tab === 'users' && <Typography variant="body2">{`${row.email} · ${row.role.replaceAll('_', ' ')} · ${row.vehicleIds.includes('*') ? 'All tenant vehicles' : `${row.vehicleIds.length} vehicles`}`}</Typography>}
                        {tab === 'readers' && (
                        <>
                          <Chip size="small" label={row.health?.replaceAll('_', ' ') || 'NEVER SEEN'} color={row.health === 'ONLINE' ? 'success' : row.health === 'OFFLINE' ? 'error' : 'default'} sx={{ my: 1 }} />
                          <Typography variant="body2">{`${recordName('checkpoints', row.checkpointId)} · ${row.identifier} · ${row.model || 'Model not specified'}`}</Typography>
                          <Typography variant="body2">{`Last heartbeat: ${row.lastSeenAt ? formatDateTime(row.lastSeenAt) : 'Not received'} · Offline after ${row.heartbeatSeconds}s`}</Typography>
                          {row.lastReadAt && <Typography variant="caption">{`Last tag read: ${formatDateTime(row.lastReadAt)}`}</Typography>}
                        </>
                        )}
                        {row.checkpointIds && <Typography variant="body2" sx={{ mt: 1 }}>{row.checkpointIds.map((id) => recordName('checkpoints', id)).join(' → ')}</Typography>}
                      </Box>
                      {AMENDABLE.includes(tab) && administer && (
                      <Stack direction="row" gap={1}>
                        <Button size="small" disabled={pending} onClick={() => startEdit(row)}>Edit</Button>
                        {tab === 'checkpoints' && <Button size="small" disabled={pending} onClick={() => setFencing(row)}>Geofence</Button>}
                        <Button size="small" color="error" disabled={pending} onClick={() => setRemoving(row)}>Delete</Button>
                      </Stack>
                      )}
                      {tab === 'assignments' && (
                      <Stack direction="row" gap={1} alignItems="center">
                        <Chip size="small" label={row.returnedAt ? 'Returned' : row.endedAt ? 'Detached' : 'Active'} />
                        {operate && (row.mode === 'TEMPORARY' || administer) && (!row.endedAt ? <Button disabled={pending} onClick={() => action(row.id, 'detach')}>Detach</Button> : !row.returnedAt && row.mode === 'TEMPORARY' && <Button disabled={pending} onClick={() => action(row.id, 'return')}>Record return</Button>)}
                      </Stack>
                      )}
                    </Stack>
                  </Paper>
                ))}
                {updatedAt && !unavailable && !(data[tab] || []).length && (
                <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
                  <Typography variant="h6">{`No ${names[tab].toLowerCase()} yet`}</Typography>
                  <Typography color="text.secondary">{canAdd ? 'Add the first record to start setting up your operation.' : 'Your administrator can set up these records.'}</Typography>
                </Paper>
                )}
              </Stack>
            )}
          {tab === 'drivers' && (
          <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6">Attendance from checkpoint activity</Typography>
            {data.drivers.map((driver) => { const days = [...new Set(trips.filter((t) => t.driverId === driver.id).flatMap((t) => t.detections.map((d) => formatDate(d.detectedAt))))]; return <Typography sx={{ mt: 1 }} key={driver.id}>{`${driver.name}: ${days.length ? days.join(', ') : 'No checkpoint activity recorded'}`}</Typography>; })}
          </Paper>
          )}
        </Box>
      </Box>
      <Dialog open={!!removing} onClose={() => !pending && setRemoving(null)} fullWidth maxWidth="xs">
        <DialogTitle>{`Delete ${removing?.name || removing?.identifier || 'this entry'}?`}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography>
            {tab === 'catalog'
              ? 'It stops being offered at the desks. Trips that already used it keep what they recorded.'
              : 'It is removed from the plant’s setup. Anything still using it will refuse the deletion and say so.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={pending} onClick={() => setRemoving(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={pending} onClick={remove}>{pending ? 'Working…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
      {fencing && <CheckpointFence checkpoint={fencing} onClose={() => setFencing(null)} />}
      {loading && <ImportRecords kind={loading} onClose={() => setLoading(null)} />}
      <Dialog open={dialog} onClose={() => !pending && setDialog(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={submit}>
          <DialogTitle>{amending ? `Edit ${amending.name || amending.identifier || names[tab]}` : tab === 'sources' ? 'Register device' : names[tab]}</DialogTitle>
          <DialogContent>
            <Stack gap={2} sx={{ pt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <FormRow>
                {(fields[tab] || []).map(([key, title, options]) => <TextField key={key} label={title} value={form[key] || ''} onChange={(e) => set(key, e.target.value)} select={!!options} type={key === 'password' ? 'password' : key.endsWith('Minutes') || key.endsWith('Kph') ? 'number' : 'text'} required={tab !== 'config' && !title.includes('optional')} inputProps={key === 'password' ? { minLength: 12, maxLength: 256 } : { maxLength: 500 }} autoComplete={key === 'password' ? 'new-password' : undefined}>{options?.map((value) => <MenuItem key={value} value={value}>{value.replaceAll('_', ' ')}</MenuItem>)}</TextField>)}
              </FormRow>
              {tab === 'sources' && form.kind === 'GPS' && (
              <>
                <TextField
                  select
                  label="GPS device"
                  helperText="Registered by the platform. Choosing it here is what links this workspace's vehicle to the unit."
                  value={form.platformDeviceId || ''}
                  onChange={(e) => set('platformDeviceId', e.target.value)}
                >
                  {available.map((d) => (
                    <MenuItem key={d.platformDeviceId} value={String(d.platformDeviceId)}>
                      {d.name && d.name !== d.identifier ? `${d.identifier} — ${d.name}` : d.identifier}
                    </MenuItem>
                  ))}
                </TextField>
                {!available.length && (
                  <Alert severity="info">
                    No GPS devices have been made available to this workspace yet. The platform registers each unit and makes it available here.
                  </Alert>
                )}
                {/* The other way a GPS unit can track: a device on the workspace's own VTS, named by
                    the id that VTS knows it by. Offered only where such a connection exists. */}
                {data.trackingConnected && !form.platformDeviceId && (
                  <TextField
                    label="Or the device id on your own VTS"
                    helperText="Use this only for a device tracked through your own VTS integration."
                    value={form.providerId || ''}
                    onChange={(e) => set('providerId', e.target.value)}
                    inputProps={{ maxLength: 20 }}
                  />
                )}
              </>
              )}
              {tab === 'users' && form.role === 'TRANSPORTER' && (
              <>
                <TextField
                  select
                  required
                  label="Transporter this account belongs to"
                  value={form.transporterId || ''}
                  onChange={(e) => set('transporterId', e.target.value)}
                >
                  {(data.transporters || []).map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
                </TextField>
                {!(data.transporters || []).length && <Alert severity="info">Add a transporter first, on the Transporters tab.</Alert>}
                <Alert severity="info">A transporter account registers and maintains its own vehicles and sees their trips. It cannot admit trucks or change plant configuration.</Alert>
              </>
              )}
              {tab === 'routes' && (
              <>
                <Alert severity="info">Select checkpoints in trip order. Start at Gate In; put the Client destination after Gate Out for Load routes.</Alert>
                <TextField select label="Ordered checkpoints" SelectProps={{ multiple: true }} value={form.checkpointIds || []} onChange={(e) => set('checkpointIds', e.target.value)}>{data.checkpoints.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}</TextField>
                <Typography variant="body2">{(form.checkpointIds || []).map((id) => recordName('checkpoints', id)).join(' → ')}</Typography>
              </>
              )}
              {tab === 'users' && (
              <TextField select label="Vehicle access" SelectProps={{ multiple: true }} value={form.vehicleIds || []} onChange={(e) => set('vehicleIds', e.target.value)}>
                <MenuItem value="*">All tenant vehicles (including future vehicles)</MenuItem>
                {data.vehicles.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
              </TextField>
              )}
              {tab === 'readers' && (
              <>
                <TextField required select label="Reader checkpoint" value={form.checkpointId || ''} onChange={(e) => set('checkpointId', e.target.value)}>{data.checkpoints.map((c) => <MenuItem key={c.id} value={c.id}>{`${c.name} · ${c.site}`}</MenuItem>)}</TextField>
                <Alert severity="info">Configure an RFID reader health integration to receive heartbeats. Offline and degraded readers raise ZENY issues.</Alert>
              </>
              )}
              {tab === 'assignments' && (
              <>
                <TextField required select label="Vehicle" value={form.vehicleId || ''} onChange={(e) => set('vehicleId', e.target.value)}>{data.vehicles.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}</TextField>
                <TextField required select label="Available tracking device" value={form.sourceId || ''} onChange={(e) => set('sourceId', e.target.value)}>{data.sources.filter((s) => deviceAvailable(s, data.assignments)).map((s) => <MenuItem key={s.id} value={s.id}>{`${s.identifier} · ${s.kind}`}</MenuItem>)}</TextField>
                <TextField select label="Assignment mode" value={form.mode} onChange={(e) => set('mode', e.target.value)}>
                  <MenuItem value="TEMPORARY">Temporary</MenuItem>
                  {(administer || data.sources.find((s) => s.id === form.sourceId)?.kind === 'RFID') && <MenuItem value="PERMANENT">Permanent</MenuItem>}
                </TextField>
              </>
              )}
              {tab === 'config' && <Alert severity="info">These are the rules in force. A blank threshold is not evaluated, so clearing one switches that rule off; rules this form does not show, such as the shift pattern, are left as they are.</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={pending} onClick={() => setDialog(false)}>Cancel</Button>
            <Button disabled={pending} type="submit" variant="contained">{pending ? 'Saving…' : 'Save'}</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </PageLayout>
  );
};
export default AdministrationPage;
